import React from 'react'
import { Card, CardContent, CardHeader, Badge, Progress } from '@/shared/ui'
import type { RewardsCycle } from '../types'
import { calcDiscountBrlFromCommissionUsdt, daysUntil } from '../utils/rewardsMath'
import { formatCurrency, formatUsdt } from '../utils/rewardsFormat'
import { getRewardsLevelProgress } from '../utils/rewardsGamification'

export default function RewardsCycleCard({
  cycle,
  multiplierX,
  usdtBrlRate,
}: {
  cycle: RewardsCycle | null
  multiplierX: number
  usdtBrlRate: number
}) {
  if (!cycle) {
    return (
      <Card className="border-0 shadow">
        <CardHeader className="border-b-0 text-base font-semibold">Seu desconto no ciclo</CardHeader>
        <CardContent className="text-sm text-foreground/70">Nenhum ciclo aberto encontrado para sua conta.</CardContent>
      </Card>
    )
  }

  const daysLeft = daysUntil(cycle.period_end)
  const estimatedDiscountBrl = calcDiscountBrlFromCommissionUsdt({
    commissionConfirmedUsdt: cycle.commission_estimated_usdt_total,
    multiplierX,
    usdtBrlRate,
  })
  const confirmedDiscountBrl = calcDiscountBrlFromCommissionUsdt({
    commissionConfirmedUsdt: cycle.commission_confirmed_usdt_total,
    multiplierX,
    usdtBrlRate,
  })

  const level = React.useMemo(() => getRewardsLevelProgress(estimatedDiscountBrl), [estimatedDiscountBrl])

  const missingVolumeUsdt = React.useMemo(() => {
    const missing = Number(level.missingDiscountBrl || 0)
    const volume = Number(cycle.volume_usdt_total || 0)
    const discount = Number(estimatedDiscountBrl || 0)
    if (!level.next) return null
    if (!Number.isFinite(missing) || missing <= 0) return null
    if (!Number.isFinite(volume) || volume <= 0) return null
    if (!Number.isFinite(discount) || discount <= 0) return null
    const discountPerVolume = discount / volume
    if (!Number.isFinite(discountPerVolume) || discountPerVolume <= 0) return null
    return missing / discountPerVolume
  }, [cycle.volume_usdt_total, estimatedDiscountBrl, level.missingDiscountBrl, level.next])

  return (
    <Card className="border-0 shadow">
      <CardHeader className="flex flex-row items-center justify-between gap-3 border-b-0 text-base font-semibold">
        <span>Seu desconto no ciclo</span>
        <Badge variant="soft" color="info">
          {daysLeft} dia(s) restantes
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-5">
          <div className="rounded-lg bg-muted/30 p-4 md:col-span-3">
            <div className="text-xs text-foreground/60">Desconto estimado (próxima fatura)</div>
            <div className="mt-1 text-3xl font-semibold">{usdtBrlRate > 0 ? formatCurrency(estimatedDiscountBrl, 'BRL') : '-'}</div>
            {usdtBrlRate > 0 && (
              <div className="mt-1 text-xs text-foreground/60">
                Garantido até agora: <span className="font-medium text-foreground">{formatCurrency(confirmedDiscountBrl, 'BRL')}</span>
              </div>
            )}
            {usdtBrlRate <= 0 && (
              <div className="mt-2 text-xs text-amber-400">
                Taxa USDT-&gt;BRL ainda nao sincronizada. Aguarde alguns minutos e recarregue a pagina.
              </div>
            )}
          </div>

          <div className="rounded-lg bg-muted/30 p-4 md:col-span-2">
            <div className="text-xs text-foreground/60">Volume no ciclo</div>
            <div className="mt-1 text-xl font-semibold">{formatUsdt(cycle.volume_usdt_total)} USDT</div>
            <div className="mt-2 text-xs text-foreground/60">Dica: mais volume = mais desconto.</div>
          </div>
        </div>

        <div className="rounded-lg bg-muted/30 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs text-foreground/60">Seu nível</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="soft" color={level.current.badgeColor}>
                  {level.current.label}
                </Badge>
                {level.next ? (
                  <span className="text-xs text-foreground/60">
                    Próximo: <span className="font-medium text-foreground">{level.next.label}</span>
                  </span>
                ) : (
                  <span className="text-xs text-foreground/60">Você chegou no nível máximo.</span>
                )}
              </div>
            </div>

            {usdtBrlRate > 0 && level.next && (
              <div className="text-right text-xs text-foreground/60">
                Meta do próximo nível:{' '}
                <span className="font-medium text-foreground">{formatCurrency(level.next.minDiscountBrl, 'BRL')}</span>
              </div>
            )}
          </div>

          {usdtBrlRate > 0 && level.next && (
            <div className="mt-3 space-y-2">
              <Progress value={level.progressPct} />
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground/60">
                <span>Faltam {formatCurrency(level.missingDiscountBrl || 0, 'BRL')} para subir de nível.</span>
                {missingVolumeUsdt ? <span>~{formatUsdt(missingVolumeUsdt)} USDT de volume adicional</span> : null}
              </div>
            </div>
          )}

          <div className="mt-3 text-xs text-foreground/60">
            O desconto é calculado por ciclo e aplicado na próxima fatura. Não acumula para o próximo ciclo.
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
